import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { useDialog } from '../components/dialogContext';
import { MutationError } from '../components/MutationError';
import { useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';
import { fmtDateTime } from './format';

const statusLabels: Record<string, string> = { PENDING: 'angefragt', RUNNING: 'läuft', COMPLETED: 'abgeschlossen', FAILED: 'fehlgeschlagen' };

// PrivacyPanel: Auskunft und Löschung/Anonymisierung – Anfrage → Wirkungsanalyse → Ausführung → Audit.
export function PrivacyPanel({ leadId }: { leadId: string }) {
  const { project, can } = useProject();
  const papi = useProjectApi();
  const queryClient = useQueryClient();
  const [showImpact, setShowImpact] = useState(false);
  const [reason, setReason] = useState('');
  const [mode, setMode] = useState<'ANONYMIZE' | 'DELETE'>('ANONYMIZE');
  const [scope, setScope] = useState<'PERSON' | 'HOUSEHOLD'>('PERSON');
  const [confirmCancel, setConfirmCancel] = useState(false);
  const enabled = can('privacy.manage');
  const impact = useQuery({
    queryKey: [...projectKeys.privacy(project.id, leadId), 'impact'],
    queryFn: ({ signal }) => papi.privacyImpact(leadId, signal),
    enabled: enabled && showImpact,
  });
  const requests = useQuery({
    queryKey: projectKeys.privacy(project.id, leadId),
    queryFn: ({ signal }) => papi.privacyRequests(leadId, signal),
    enabled,
    refetchInterval: (q) => (q.state.data?.items.some((r) => r.status === 'PENDING' || r.status === 'RUNNING') ? 3000 : false),
  });
  const done = () => {
    setReason('');
    void queryClient.invalidateQueries({ queryKey: projectKeys.privacy(project.id, leadId) });
  };
  const exportReq = useMutation({ mutationFn: () => papi.privacyExport(leadId, reason), onSuccess: done });
  const dialog = useDialog();
  const erase = useMutation({
    mutationFn: () => papi.privacyErase(leadId, { mode, scope, reason, cancel_future_appointments: confirmCancel }),
    onSuccess: done,
  });
  if (!enabled) return null;
  const im = impact.data;
  const future = im ? (scope === 'HOUSEHOLD' ? im.household_future_appointments : im.future_appointments) : 0;
  return (
    <section className="card privacy-panel" aria-labelledby={`privacy-${leadId}`}>
      <h2 id={`privacy-${leadId}`}>Datenschutz</h2>
      {!showImpact ? (
        <button type="button" className="btn btn-small" onClick={() => setShowImpact(true)}>
          Auskunft / Löschung vorbereiten
        </button>
      ) : impact.isPending ? (
        <p className="muted">Wirkungsanalyse läuft …</p>
      ) : impact.isError ? (
        <MutationError error={impact.error} />
      ) : im ? (
        <>
          <p className="muted">Betroffene Daten (Wirkungsanalyse):</p>
          <ul className="inline-list">
            <li>{im.calls} Anrufe</li>
            <li>{im.recordings} Aufzeichnungen</li>
            <li>{im.transcripts} Transkripte</li>
            <li>{im.appointments} Termine</li>
            <li>{im.tickets} Rückfragen</li>
            <li>{im.emails} E-Mails</li>
            <li>{im.activities} Historieneinträge</li>
          </ul>
          {(im.objects > 1 || im.channels > 0) && (
            <p className="muted">
              {im.objects > 1 && <>Die Person hat {im.objects} Objekte – alle werden gelöscht. </>}
              {im.channels > 0 && (
                <>
                  Ihre {im.channels === 1 ? 'Nummer bzw. E-Mail-Adresse kommt' : `${im.channels} Nummern bzw. E-Mail-Adressen kommen`} dauerhaft auf
                  die Sperrliste: Darüber wird niemand mehr angerufen, auch kein anderes Haushaltsmitglied.
                </>
              )}
            </p>
          )}
          {im.channel_leads > 0 && (
            <p className="alert alert-warn">
              Unter dieser Nummer bzw. E-Mail-Adresse {im.channel_leads === 1 ? 'ist 1 weiterer Lead' : `sind ${im.channel_leads} weitere Leads`}{' '}
              erreichbar (Haushalt).
            </p>
          )}
          {im.blockers.map((b) => (
            <p key={b} className="alert alert-warn">
              {b}
            </p>
          ))}
          {!im.already_anonymized && (
            <form className="inline-panel" onSubmit={(e) => e.preventDefault()}>
              <label className="field wide">
                <span>Anlass der Anfrage (Pflicht, z. B. „Auskunftsersuchen vom 01.09.“)</span>
                <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} required />
              </label>
              <div className="form-grid">
                <label className="field">
                  <span>Umfang</span>
                  <select value={scope} onChange={(e) => setScope(e.target.value as 'PERSON' | 'HOUSEHOLD')}>
                    <option value="PERSON">
                      {im.channel_leads > 0 ? 'Diese Person (beim Haushalt nur Nummer bzw. E-Mail entfernen)' : 'Diese Person'}
                    </option>
                    {im.channel_leads > 0 && <option value="HOUSEHOLD">Ganzer Haushalt („unsere Daten“) – {im.household_objects} Leads</option>}
                  </select>
                </label>
                <label className="field">
                  <span>Löschart</span>
                  <select value={mode} onChange={(e) => setMode(e.target.value as 'ANONYMIZE' | 'DELETE')}>
                    <option value="ANONYMIZE">Anonymisieren (Statistik bleibt)</option>
                    <option value="DELETE">Anonymisieren und aus Listen entfernen</option>
                  </select>
                </label>
                {future > 0 && (
                  <label className="checkbox">
                    <input type="checkbox" checked={confirmCancel} onChange={(e) => setConfirmCancel(e.target.checked)} /> Zukünftige Termine absagen
                  </label>
                )}
              </div>
              {(exportReq.isError || erase.isError) && <MutationError error={exportReq.error ?? erase.error} />}
              <div className="form-actions">
                <button type="button" className="btn" disabled={!reason.trim() || exportReq.isPending} onClick={() => exportReq.mutate()}>
                  Auskunft erstellen
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  disabled={!reason.trim() || erase.isPending || (future > 0 && !confirmCancel)}
                  onClick={async () => {
                    const ok = await dialog.confirm({
                      title: 'Daten endgültig entfernen?',
                      message:
                        scope === 'HOUSEHOLD'
                          ? `Die Daten aller ${im.household_objects} Leads des Haushalts werden anonymisiert, Aufnahmen und Dokumente gelöscht, Nummern und E-Mail-Adressen dauerhaft gesperrt. Das lässt sich nicht rückgängig machen.`
                          : 'Die Daten dieser Person (alle ihre Objekte) werden anonymisiert, Aufnahmen und Dokumente gelöscht, ihre Nummern und E-Mail-Adressen dauerhaft gesperrt. Das lässt sich nicht rückgängig machen.',
                      confirmLabel: 'Daten entfernen',
                      danger: true,
                    });
                    if (ok) erase.mutate();
                  }}
                >
                  Daten entfernen
                </button>
              </div>
            </form>
          )}
        </>
      ) : null}
      {(requests.data?.items.length ?? 0) > 0 && (
        <ul className="plain-list">
          {requests.data?.items.map((r) => (
            <li key={r.id}>
              {r.type === 'EXPORT' ? 'Auskunft' : 'Löschung'} · {statusLabels[r.status] ?? r.status} · {fmtDateTime(r.created_at)}
              {r.has_export && (
                <>
                  {' '}
                  ·{' '}
                  <a href={papi.privacyDownloadUrl(r.id)} download>
                    Export herunterladen
                  </a>
                </>
              )}
              {r.error && <span className="text-error"> · {r.error}</span>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
